import * as React from 'react'
import { Drawer, Timeline, Result, Row, Tag, message } from 'antd'
import { Currency } from '../../../../../components/Currency'
import { Link } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { INestedProduct, InvoiceProductCostHistory, InvoiceStatus } from '@getgreenline/homi-shared'
import moment from 'moment'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import { LoadingAnimation } from '../../../../../components/LoadingAnimation'
import { SmartImage } from '../../../../../components/SmartImage'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { parseErrorMsg } from '../../../../../utilities/helpers'

interface Props {
  isDrawerVisible: boolean
  setIsDrawerVisible: (isDrawerVisible: boolean) => void
  product: INestedProduct
  costHistory: InvoiceProductCostHistory[] | undefined
  hasMore: boolean
  getCostHistory: (productId: string) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

export const CostPerUnitHistoryDrawer = inject('currentCompanyStore')(
  (props: Props & InjectedProps) => {
    const {
      isDrawerVisible,
      setIsDrawerVisible,
      product,
      hasMore,
      getCostHistory,
      currentCompanyStore,
    } = props
    const companyId = currentCompanyStore!.company!.id

    const [isLoading, setIsLoading] = React.useState(false)

    const loadMore = async () => {
      try {
        setIsLoading(true)
        await getCostHistory(product.id)
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while loading more cost history')
      } finally {
        setIsLoading(false)
      }
    }

    const renderStatus = (status: InvoiceStatus) => {
      const statusObject = { name: '', color: '' }

      switch (true) {
        case status === InvoiceStatus.PENDING:
          statusObject.name = 'Pending'
          statusObject.color = 'silver'
          break
        case status === InvoiceStatus.IN_PROGRESS:
          statusObject.name = 'In progress'
          statusObject.color = 'orange'
          break
        case status === InvoiceStatus.COMPLETED:
          statusObject.name = 'Completed'
          statusObject.color = 'green'
          break
        default:
          statusObject.name = 'Cancelled'
          statusObject.color = 'red'
      }

      return statusObject
    }

    const renderCostHistory = (costHistory: InvoiceProductCostHistory[]) => {
      if (costHistory.length === 0) {
        return <Result status='info' title='No cost history available' />
      } else {
        return (
          <Timeline>
            {costHistory.map((historyItem, index) => {
              const { color, name } = renderStatus(historyItem.status)

              return (
                <Timeline.Item key={index} color={color}>
                  <Row>
                    <Link
                      target='_blank'
                      to={Routes.EDIT_INVOICE(companyId, historyItem.invoiceId)}
                    >
                      {historyItem.invoiceId}
                    </Link>
                    <Tag className='ml-2' color={color}>
                      {name}
                    </Tag>
                  </Row>

                  <Row>
                    {historyItem.quantityReceived === null ? (
                      <span>
                        <b>No</b> units received
                      </span>
                    ) : (
                      <span>
                        <b>{`${historyItem.quantityReceived}`}</b>
                        <span> {historyItem.quantityReceived > 1 ? 'units' : 'unit'}</span>
                      </span>
                    )}
                  </Row>

                  <Row>
                    <b>
                      <Currency centValue={historyItem.pricePerUnit} />
                    </b>
                    <span> per unit</span>
                  </Row>

                  <Row className='text-muted text-right'>
                    <small>
                      <span>Create date: </span>
                      {moment(historyItem.createDate).format('LLL')}
                    </small>
                  </Row>

                  {historyItem.completeDate && (
                    <Row className='text-muted text-right'>
                      <small>
                        <span>Complete date: </span>
                        {moment(historyItem.completeDate).format('LLL')}
                      </small>
                    </Row>
                  )}
                </Timeline.Item>
              )
            })}
          </Timeline>
        )
      }
    }

    return (
      <Drawer
        title='Cost history'
        maskClosable
        placement='right'
        closable={false}
        width={350}
        onClose={() => setIsDrawerVisible(false)}
        visible={isDrawerVisible}
      >
        <div className='mb-4'>
          <div className='d-flex align-items-center'>
            {product.imageUrl && (
              <div className='mr-3'>
                <SmartImage width={64} height={64} src={product.imageUrl} />
              </div>
            )}

            <span>
              <b>
                {product.parentProductName
                  ? `${product.parentProductName} - ${product.name}`
                  : product.name}
              </b>
              {product.sku !== null && <div className='text-muted'>{`SKU: ${product.sku}`}</div>}
              {product.barcode !== null && (
                <div className='text-muted'>{`Barcode: ${product.barcode}`}</div>
              )}
              {product.price !== null && (
                <div className='text-muted'>
                  Retail price: <Currency centValue={product.price} />
                </div>
              )}
            </span>
          </div>

          <hr className='mt-4 mb-4' style={{ marginLeft: '-24px', marginRight: '-24px' }} />

          {props.costHistory === undefined ? (
            <LoadingAnimation />
          ) : (
            renderCostHistory(props.costHistory)
          )}

          {hasMore && (
            <BlazeButton
              buttonProps={{
                className: 'w-100',
                loading: isLoading,
                disabled: isLoading,
                onClick: loadMore,
              }}
              titleProps={{
                title: 'Load more',
              }}
            />
          )}
        </div>
      </Drawer>
    )
  },
)
